import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Play, History, Search, User, Building2, Crown } from "lucide-react";
import MatchesTab from "@/components/matches-tab";
import RankingTab from "@/components/ranking-tab";
import HistoryTab from "@/components/history-tab";
import PlayersTab from "@/components/players-tab";
import ClubsTab from "@/components/clubs-tab";
import NotificationDropdown from "@/components/notification-dropdown";
import NotificationPanel from "@/components/notification-panel";
import SpotlightTour from "@/components/spotlight-tour";
import { NotificationPermission } from "@/components/notification-permission";
import Profile from "@/pages/profile";
import { apiRequest } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNotifications } from "@/hooks/use-notifications";
import { Bell, HelpCircle } from "lucide-react";
import type { Player } from "../../../shared/schema";
import { useTranslation } from "react-i18next";

interface DashboardProps {
  onLogout: () => void;
  onProfileClick: (section?: string) => void;
  playerId: string | null;
  onShowTour?: () => void;
}

export default function Dashboard({ onLogout, onProfileClick, playerId, onShowTour }: DashboardProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (tabParam && ['matches', 'ranking', 'clubs', 'history', 'players'].includes(tabParam)) {
        return tabParam;
      }
    }
    return "ranking";
  });
  const [initialSearchQuery, setInitialSearchQuery] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('search') || undefined;
    }
    return undefined;
  });
  const [showOnboardingDemo, setShowOnboardingDemo] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [targetClubId, setTargetClubId] = useState<string | null>(null);
  const isMobile = useIsMobile();


  // Fetch player data to check premium status
  // Only fetch if playerId exists and we have a valid token
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem("authToken");
  const { data: player } = useQuery<Player>({
    queryKey: ["/api/players", playerId],
    queryFn: async () => {
      if (!playerId || !hasToken) return null;
      const response = await apiRequest("GET", `/api/players/${playerId}`);
      return response.json();
    },
    enabled: !!playerId && hasToken,
  });

  // SpotlightTour gère son propre affichage basé sur le localStorage
  // On ne le lance plus avec un setTimeout global ici


  const renderActiveTab = () => {
    switch (activeTab) {
      case "matches":
        return <MatchesTab selectedPlayerId={playerId} />;
      case "ranking":
        return <RankingTab playerId={playerId} />;
      case "clubs":
        return <ClubsTab
          selectedPlayerId={playerId}
          initialEditClubId={targetClubId}
          onClearEditClubId={() => setTargetClubId(null)}
          initialSearchQuery={initialSearchQuery}
        />;
      case "history":
        return <HistoryTab playerId={playerId} />;
      case "players":
        return <PlayersTab playerId={playerId} onNavigateToClub={(clubName) => {
          setInitialSearchQuery(clubName);
          setActiveTab("clubs");
        }} />;
      default:
        return <RankingTab playerId={playerId} />;
    }
  };

  // Ancien code demo supprimé

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setShowNotifications(false);
    setShowProfile(false);
    setInitialSearchQuery(undefined); // Clear search query so child components don't re-initialize with stale data

    // Clean up URL parameters (e.g., ?tab=clubs&search=...) to avoid state desync on refresh/share
    if (typeof window !== 'undefined' && window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const handleNotificationsClick = () => {
    setShowNotifications(!showNotifications);
    setShowProfile(false);
    if (!showNotifications) {
      setActiveTab(""); // Clear active tab when showing notifications
    }
  };

  const handleProfileClickDesktop = (section?: string) => {
    setShowProfile(!showProfile);
    setShowNotifications(false);
    if (!showProfile) {
      setActiveTab(""); // Clear active tab when showing profile
    }
    // If a section is provided, ensure we pass it up
    if (section) {
      onProfileClick(section);
    }
  };

  const handleShowTour = () => {
    setShowProfile(false);
    setShowNotifications(false);

    // Restart tour for current tab
    if (typeof window !== 'undefined' && typeof (window as any).restartPadeloTour === 'function') {
      (window as any).restartPadeloTour(activeTab || 'ranking');
    }

    onShowTour?.();
  };

  const { unreadCount } = useNotifications(playerId);

  const tabs = [
    { id: "matches", label: t('nav.matches'), icon: Play },
    { id: "ranking", label: t('nav.ranking'), icon: Trophy },
    { id: "clubs", label: t('nav.clubs'), icon: Building2 },
    { id: "history", label: t('nav.history'), icon: History },
    { id: "players", label: t('nav.players'), icon: Search },
  ];

  // Mobile layout: vertical with bottom navigation
  if (isMobile) {
    return (
      <div className="w-full sm:max-w-md sm:mx-auto bg-background shadow-lg h-[100dvh] flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-padel-green text-white p-3 sm:p-4 flex-shrink-0 z-50 shadow-lg safe-area-top">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Trophy className={`text-2xl ${player?.isPremium ? 'text-yellow-400' : 'text-white'}`} data-testid="logo-icon" />
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-bold" data-testid="app-title">PadELO</h1>
              </div>
            </div>
            <div className="flex items-center space-x-2">

              <NotificationDropdown
                playerId={playerId}
                onNavigateToMatches={() => setActiveTab("matches")}
                onNavigateToProfile={(section) => onProfileClick(section)}
                onNavigateToClubs={(clubId) => {
                  setActiveTab("clubs");
                  if (clubId) setTargetClubId(clubId);
                }}
                onNavigateToPlayers={() => setActiveTab("players")}
              />
              <button
                onClick={() => onProfileClick()}
                className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors dark-btn-active"
                data-testid="button-profile"
                title="Profile"
              >
                <User className="text-sm" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto" data-testid="main-content" style={{ paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px))' }}>
          {/* Notification Permission Modal */}
          {playerId && (
            <NotificationPermission
              playerId={playerId}
              onPermissionGranted={() => {
                console.log('Notification permission granted');
              }}
            />
          )}

          {renderActiveTab()}
        </main>

        {/* Bottom Navigation */}
        <nav className="bg-padel-green text-white border-t border-border z-50 flex-shrink-0 safe-area-bottom" data-testid="nav-tabs">
          <div className="flex justify-around items-center px-2 py-2" style={{ minHeight: '64px' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex flex-col items-center space-y-1 px-2 py-1 rounded-lg transition-all relative ${activeTab === tab.id
                  ? 'text-white'
                  : 'text-white opacity-70 hover:opacity-100'
                  }`}
                data-testid={`tab-${tab.id}`}
              >
                {/* Subtle top indicator for active tab on mobile */}
                {activeTab === tab.id && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-white rounded-full" />
                )}
                {tab.id === "clubs" ? (
                  <img
                    src="/tennis-ball.png"
                    alt="Tennis Ball"
                    className="w-5 h-5 filter brightness-0 invert"
                  />
                ) : (
                  <tab.icon className="text-lg" />
                )}
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Spotlight Tour */}
        <SpotlightTour activeTab={activeTab} />
      </div>
    );
  }

  // Desktop/Tablet layout: horizontal with sidebar
  return (
    <div className="w-full h-screen flex overflow-hidden bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-padel-green text-white flex flex-col flex-shrink-0 shadow-lg">
        {/* Logo Section */}
        <div className="p-4 border-b border-white border-opacity-20">
          <div className="flex items-center space-x-3">
            <Trophy className={`text-2xl ${player?.isPremium ? 'text-yellow-400' : 'text-white'}`} data-testid="logo-icon" />
            <div className="flex items-center space-x-2">
              <h1 className="text-xl font-bold" data-testid="app-title">PadELO</h1>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4" data-testid="nav-tabs">
          <div className="space-y-1 px-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all relative ${activeTab === tab.id && !showNotifications && !showProfile
                  ? 'text-white'
                  : 'text-white opacity-70 hover:opacity-100 hover:bg-white hover:bg-opacity-5'
                  }`}
                data-testid={`tab-${tab.id}`}
              >
                {/* Subtle left border indicator for active tab */}
                {activeTab === tab.id && !showNotifications && !showProfile && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                )}
                {tab.id === "clubs" ? (
                  <img
                    src="/tennis-ball.png"
                    alt="Tennis Ball"
                    className="w-5 h-5 filter brightness-0 invert"
                  />
                ) : (
                  <tab.icon className="text-lg" />
                )}
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}

            {/* Notifications Button */}
            <button
              onClick={handleNotificationsClick}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all relative ${showNotifications
                ? 'text-white'
                : 'text-white opacity-70 hover:opacity-100 hover:bg-white hover:bg-opacity-5'
                }`}
              data-testid="nav-notifications"
            >
              {/* Subtle left border indicator for active tab */}
              {showNotifications && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
              )}
              <Bell className="text-lg" />
              <span className="font-medium">{t('nav.notifications')}</span>
              {unreadCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {/* Profile Button */}
            <button
              onClick={() => handleProfileClickDesktop()}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all relative ${showProfile
                ? 'text-white'
                : 'text-white opacity-70 hover:opacity-100 hover:bg-white hover:bg-opacity-5'
                }`}
              data-testid="nav-profile"
            >
              {/* Subtle left border indicator for active tab */}
              {showProfile && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
              )}
              <User className="text-lg" />
              <span className="font-medium">{t('nav.profile')}</span>
            </button>


          </div>
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-background w-full" data-testid="main-content">
          {/* Notification Permission Modal */}
          {playerId && (
            <NotificationPermission
              playerId={playerId}
              onPermissionGranted={() => {
                console.log('Notification permission granted');
              }}
            />
          )}

          {/* Show Notifications Panel */}
          {showNotifications ? (
            <NotificationPanel
              playerId={playerId}
              onNavigateToMatches={() => {
                setShowNotifications(false);
                setActiveTab("matches");
              }}
              onNavigateToProfile={(section) => {
                handleProfileClickDesktop(section);
              }}
              onNavigateToPlayers={() => {
                setShowNotifications(false);
                setActiveTab("players");
              }}
              onClose={() => setShowNotifications(false)}
            />
          ) : showProfile ? (
            <Profile
              onLogout={onLogout}
              onBack={() => setShowProfile(false)}
              playerId={playerId}
              onShowTour={handleShowTour}
            />
          ) : (
            renderActiveTab()
          )}
        </main>
      </div>

      {/* Spotlight Tour */}
      <SpotlightTour activeTab={activeTab} />
    </div>
  );
}
